import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { accountingGateway } from '../index';
import { ALL_GL_CATEGORIES, glCategoryLabels, type GlMappingResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

export function GlMappingsPage() {
  const [mappings, setMappings] = useState<Map<number, GlMappingResponse>>(new Map());
  const [codes, setCodes] = useState<Record<number, string>>({});
  const [names, setNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await accountingGateway.getGlMappings();
      const byCategory = new Map(rows.map((r) => [r.category, r]));
      setMappings(byCategory);
      setCodes(Object.fromEntries(ALL_GL_CATEGORIES.map((c) => [c, byCategory.get(c)?.glAccountCode ?? ''])));
      setNames(Object.fromEntries(ALL_GL_CATEGORIES.map((c) => [c, byCategory.get(c)?.glAccountName ?? ''])));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load GL mappings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const rows = await accountingGateway.setGlMappings(
        ALL_GL_CATEGORIES.map((category) => ({ category, glAccountCode: codes[category] ?? '', glAccountName: names[category] ?? '' })),
      );
      setMappings(new Map(rows.map((r) => [r.category, r])));
      setSaved(true);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save GL mappings.'));
    } finally {
      setSaving(false);
    }
  }

  const isFullyMapped = mappings.size === ALL_GL_CATEGORIES.length;

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>GL Mappings</h2>
        {!loading && (
          <span className="muted">{isFullyMapped ? 'All 8 categories mapped' : `${mappings.size} of ${ALL_GL_CATEGORIES.length} categories mapped`}</span>
        )}
      </div>

      <p className="muted">
        Every category below must have a GL account before a payroll run can be exported. Saving replaces the
        full set of mappings for this company.
      </p>

      {error && <p className="error">{error}</p>}
      {saved && !error && <p className="success">GL mappings saved.</p>}

      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <form onSubmit={handleSubmit}>
          <table>
            <thead>
              <tr><th>Category</th><th>GL Account Code</th><th>GL Account Name</th></tr>
            </thead>
            <tbody>
              {ALL_GL_CATEGORIES.map((category) => (
                <tr key={category}>
                  <td>{glCategoryLabels[category]}</td>
                  <td>
                    <input
                      value={codes[category] ?? ''}
                      onChange={(e) => setCodes((c) => ({ ...c, [category]: e.target.value }))}
                      placeholder="e.g. 6000"
                      required
                    />
                  </td>
                  <td>
                    <input
                      value={names[category] ?? ''}
                      onChange={(e) => setNames((n) => ({ ...n, [category]: e.target.value }))}
                      placeholder="e.g. Wage Expense"
                      required
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save mappings'}</button>
        </form>
      )}
    </section>
  );
}
